import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { KeyRound, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getRuntimeConfig } from '@nucleus-commerce/admin-kit/config';
import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { apiKeysQueryOptions, useCreateApiKey } from '@/features/api-keys';

import { useDocsKeys } from '../../hooks/use-docs-keys';

import { KeyList } from './key-list';
import { keyStatus } from './key-status';
import { PasteKeyForm } from './paste-key-form';
import { StorePicker } from './store-picker';

/** Key prefixes are the first 12 characters, as the backend stores them. */
const prefixOf = (key: string) => key.slice(0, 12);

function CreatePlaygroundKey({
  storeId,
  onCreated,
}: {
  storeId: string;
  onCreated: (created: { prefix: string; key: string; name: string; createdAt: string }) => void;
}) {
  const { t } = useTranslation('docs');
  const message = useErrorMessage();
  const { can } = usePermissions();
  const create = useCreateApiKey(storeId);
  const allowed = can('api_keys.manage');
  const origin = window.location.origin;

  const mint = async () => {
    const created = await create.mutateAsync({
      name: t('keys.create.name'),
      kind: 'publishable',
      allowedOrigins: [origin],
      scopes: [],
    });
    const { prefix, name, createdAt } = created.apiKey;
    onCreated({ prefix, key: created.key, name, createdAt });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Button
        type="button"
        variant="secondary"
        disabled={!allowed || create.isPending}
        onClick={() => void mint().catch(() => undefined)}
      >
        <KeyRound aria-hidden />
        {create.isPending ? t('keys.create.pending') : t('keys.create.action')}
      </Button>
      <p className="text-muted-foreground text-xs">
        {allowed ? t('keys.create.hint', { origin }) : t('keys.create.noPermission')}
      </p>
      {create.error ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{message(create.error)}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}

/**
 * Picks the store and the key the playground calls with (plan 9.1). Keys live in this browser
 * only; with `api_keys.manage` the store's key list marks the ones revoked or deleted since.
 */
export function KeyPanel() {
  const { t } = useTranslation('docs');
  const { can } = usePermissions();
  const docsKeys = useDocsKeys();
  const { storeId, keys, selectedKey } = docsKeys;
  const storeKeys = useQuery({
    ...apiKeysQueryOptions(storeId ?? ''),
    enabled: storeId !== null && can('api_keys.manage'),
  });

  if (!storeId) {
    return <p className="text-muted-foreground text-sm">{t('keys.noStores')}</p>;
  }

  const status = (key: (typeof keys)[number]) => keyStatus(key, storeKeys.data);
  const selectedStatus = selectedKey ? status(selectedKey) : 'unknown';

  return (
    <div className="flex flex-col gap-4">
      <StorePicker stores={docsKeys.stores} storeId={storeId} onChange={docsKeys.selectStore} />
      <KeyList
        keys={keys}
        selected={selectedKey?.prefix ?? null}
        status={status}
        onSelect={docsKeys.selectKey}
        onRemove={docsKeys.removeKey}
      />
      {selectedStatus === 'revoked' || selectedStatus === 'missing' ? (
        <Alert variant="destructive">
          <TriangleAlert aria-hidden />
          <AlertDescription>
            <span>
              {t('keys.revokedHint')}{' '}
              <Link
                to="/s/$storeId/settings/api-keys"
                params={{ storeId }}
                className="font-medium underline underline-offset-4"
              >
                {t('keys.manage')}
              </Link>
            </span>
          </AlertDescription>
        </Alert>
      ) : null}
      <CreatePlaygroundKey storeId={storeId} onCreated={docsKeys.addKey} />
      <PasteKeyForm
        onAdd={(key) =>
          docsKeys.addKey({
            prefix: prefixOf(key),
            key,
            name: t('keys.paste.name'),
            createdAt: new Date().toISOString(),
          })
        }
      />
      <dl className="text-muted-foreground grid gap-0.5 text-xs">
        <dt className="font-medium">{t('keys.baseUrl')}</dt>
        <dd className="text-foreground font-mono break-all">{getRuntimeConfig().apiBaseUrl}</dd>
      </dl>
      <p className="text-muted-foreground text-xs">{t('keys.storedLocally')}</p>
    </div>
  );
}
